import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { useCurrencyContext } from '@/hooks/useCurrencyContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CurrencyInput } from '@/components/ui/CurrencyInput';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { autoCategorize, categoriesForType, CATEGORY_COLORS, isRefundDescription } from '@/lib/categorize';
import { CategorySelect } from '@/components/CategorySelect';
import { CategoryCombobox } from '@/components/CategoryCombobox';
import { ActiveFilterChips } from '@/components/transactions/ActiveFilterChips';
import CategoryRemapNoticeBanner from '@/components/CategoryRemapNoticeBanner';
import UploadReminderBanner from '@/components/UploadReminderBanner';
import ReconciliationBanner from '@/components/ReconciliationBanner';
import useUploadReminder from '@/hooks/useUploadReminder';
import { formatMoney, todayISO, currencySymbol, CURRENCIES } from '@/lib/format';
import { resolveDefaultCurrency, suggestCurrency } from '@/lib/defaultCurrency';
import { prettifyDescription, modeLabel } from '@/lib/txDescription';
import { toast } from 'sonner';
// BUG-002 — the one shared income rule (category set + loan-drawdown narrations).
import { isRealIncome, isUnresolvedInflow } from '@/lib/interimPolicy';
import { Plus, Search, Trash2, ArrowDownRight, ArrowUpRight, TrendingUp, Upload, ScanLine, Loader2, Sparkles, Pencil, ArrowLeftRight, Wallet, Calendar, RotateCcw, Shield } from 'lucide-react';
import { TransactionAttribution, TransactionFlagButton } from '@/components/TransactionFlag';
import { hasActivePartner } from '@/lib/workspaces';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { slugify as slugifyCategory } from '@/lib/detailHelpers';
import { insertTransactionIdempotent } from '@/lib/insertTransaction';
import axios from 'axios';

import usePageTitle from '@/hooks/usePageTitle';
import useFocusRefetch from '@/hooks/useFocusRefetch';
import { readCache, writeCache, withTimeout } from '@/lib/pageCache';
import { RANGE_PRESETS, resolveRange, txnInRange } from '@/lib/dateRange';

// Phase 3 — Transactions Redesign
// Extracted UI subcomponents live under /components/transactions/.
// The heavy state (filters, mutations, add-modal) stays here in
// Transactions.js; the row / drawer / bulk-bar / attention-band are
// presentational and take the callbacks they need.
import TxnRow            from '@/components/transactions/TxnRow';
import TxnDrawer         from '@/components/transactions/TxnDrawer';
import TxnBulkActionBar  from '@/components/transactions/TxnBulkActionBar';
import TxnAttentionBand  from '@/components/transactions/TxnAttentionBand';
import { groupByDay, dayLabel, confidenceOf } from '@/components/transactions/txnHelpers';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TYPES = [
  { value: 'expense',    label: 'Expense',    icon: ArrowUpRight },
  { value: 'income',     label: 'Income',     icon: ArrowDownRight },
  { value: 'investment', label: 'Investment', icon: TrendingUp },
  { value: 'transfer',   label: 'Transfer',   icon: ArrowLeftRight },
  { value: 'refund',     label: 'Refund',     icon: RotateCcw },
];

function monthLabel(yyyyMm) {
  // yyyyMm is 'YYYY-MM'. Render as 'May 2026'.
  if (!yyyyMm || !/^\d{4}-\d{2}$/.test(yyyyMm)) return yyyyMm;
  const [y, m] = yyyyMm.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

// Phase 84.19 — Bug 3 fix.
// PostgREST silently caps single responses at 1000 rows (db-max-rows).
// A single `.limit(500)` on the transactions table dropped every row
// older than the 500th most-recent for users with heavy import history.
// This helper paginates via `.range()` so the client sees every row it
// is entitled to. Ordered ascending by txn_date so the returned array
// is deterministic across pages; downstream code sorts as needed.
async function fetchAllTransactions() {
  const PAGE = 1000;
  const CAP  = 50_000; // paranoia — no legitimate user has this many
  let rows = [];
  let offset = 0;
  while (offset < CAP) {
    const { data, error } = await supabase
      .from('transactions')
      .select('*')
      .order('txn_date', { ascending: true })
      .order('id', { ascending: true })  // stable tiebreak within same date
      .range(offset, offset + PAGE - 1);
    if (error) return { data: null, error };
    if (!Array.isArray(data) || data.length === 0) break;
    rows = rows.concat(data);
    if (data.length < PAGE) break;
    offset += PAGE;
  }
  // The rest of Transactions.js expects newest-first; reverse once at
  // the boundary so downstream sorts / group-by-day keep working.
  return { data: rows.reverse(), error: null };
}

export default function Transactions() {
  usePageTitle('Transactions');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, profile, session } = useAuth();
  // Phase 84.22.5 — Transactions can live across multiple currencies
  // (INR salary, AED rent, USD investment). The headline KPIs must show
  // sums in the user's BASE currency, so each txn is converted through
  // `convertToBase(amount, accountCurrency)` before we aggregate. The
  // individual row still displays in the account's native currency
  // (see `TxnRow` below) — native amounts stay sacred.
  const baseCurrency = (profile?.base_currency || profile?.currency || 'INR').toUpperCase();
  const currency = baseCurrency; // legacy alias retained for downstream call sites

  // Phase 30, Safari tab-suspension resilience. Hydrate from sessionStorage
  // so a remount after a long background never shows an empty state.
  const uid = user?.id || null;
  const cached = uid ? (readCache(uid, 'transactions:v1') || {}) : {};

  const [txns, setTxns] = useState(cached.txns || []);
  const [accounts, setAccounts] = useState(cached.accounts || []);
  // Phase 55 Batch B, load the user's saved category rules (created via
  // the import "Remember this for next time?" flow) so manual entries on
  // this page benefit from the same auto-categorization. Longest pattern
  // wins (more specific over generic) to mirror backend behavior.
  const [merchantRules, setMerchantRules] = useState(cached.merchantRules || []);
  const [loading, setLoading] = useState(!cached.txns);
  const hasLoadedOnceRef = useRef(!!cached.txns);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCat, setFilterCat] = useState('all');
  // Phase 51.2, Account filter. Defaults to 'all' so the page behaves
  // identically for users who don't care about account-scoped views.
  const [filterAccountId, setFilterAccountId] = useState('all');
  // Phase 84.20 (Beta issue #6) — Sort mode for the transactions list.
  //   'date_desc' (default) → newest first (legacy behaviour)
  //   'date_asc'            → oldest first
  //   'amount_desc'         → biggest magnitude first (High → Low)
  //   'amount_asc'          → smallest magnitude first (Low → High)
  //   'merchant_asc'        → alphabetical by description (A → Z)
  // We sort on `Math.abs(amount)` because on-disk amounts are signed
  // (expense is negative). Users think of amounts as magnitudes.
  const [sortMode, setSortMode] = useState('date_desc');
  // Date range, defaults to "This month" per product spec.
  const [rangePreset, setRangePreset] = useState('this_month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [customOpen, setCustomOpen] = useState(false);
  // Phase 84.23.11 — Custom picker supports two shapes: 'range' (mm/dd
  // /yyyy From + To) or 'month' (a 3×4 month grid). Beta users often
  // just want "September" — the month shape is 1 click, not two dates.
  const [customMode, setCustomMode] = useState('month');
  const [customMonthYear, setCustomMonthYear] = useState(() => new Date().getFullYear());
  const [customMonthSel, setCustomMonthSel] = useState(() => ({
    year: new Date().getFullYear(),
    month: new Date().getMonth(),
  }));
  const [open, setOpen] = useState(false);
  // v3.4.6 — description autocomplete suggestions, seeded once when the
  // page loads and refreshed when the Add dialog opens. Fed to a native
  // <datalist> attached to the description input so the browser handles
  // filtering/scrolling for free.
  const [descriptionSuggestions, setDescriptionSuggestions] = useState([]);
  const [editingId, setEditingId] = useState(null);
  // Phase 61A — capture the "before" kind/category at openForEdit time so the
  // post-save log-signal call can describe how the user reclassified this
  // merchant. Held in a ref to avoid extra renders.
  const editOriginalsRef = useRef({ before_kind: null, before_category: null });
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  // Phase 81.3 — lazy Cash Wallet creation state (inline button in Add
  // Transaction dialog when payment_method='cash' and no cash account
  // exists). We deliberately do NOT auto-create a Cash Wallet for every
  // user — it would add clutter for users who never handle cash.
  const [creatingCashWallet, setCreatingCashWallet] = useState(false);
  // A proposed currency for a new Cash Wallet, awaiting confirmation. Never a
  // silent default: see lib/defaultCurrency.js for the policy.
  const [walletProposal, setWalletProposal] = useState(null);
  const fileRef = useRef(null);

  // Phase 41.1, Detect whether the current user has any "real" partner
  // (i.e. a workspace with > 1 accepted member). Used to gate the
  // flag-for-review affordance with a calm "invite first" dialog.
  // We probe once on mount; the flag component receives the result via prop.
  const [hasPartner, setHasPartner] = useState(null); // null | true | false
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!session?.access_token) return;
      try {
        const v = await hasActivePartner(session.access_token);
        if (!cancelled) setHasPartner(!!v);
      } catch {
        if (!cancelled) setHasPartner(false);
      }
    })();
    return () => { cancelled = true; };
    // Re-probe when the workspace surface changes, stable user id only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id]);

  // Phase 80 — Upload reminder banner. Fetches the same snapshot used by
  // the dashboard tile (server-side deterministic + cached fast), then
  // runs the shared hook to derive the reason + copy.
  const [uploadSnapshot, setUploadSnapshot] = useState(null);
  useEffect(() => {
    if (!session?.access_token) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/snapshot`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
          timeout: 12000,
        });
        if (!cancelled) setUploadSnapshot(r.data || null);
      } catch (e) {
        // Banner is best-effort; missing snapshot just hides the reminder.
        console.warn('upload reminder snapshot fetch failed:', e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [session?.access_token]);
  const uploadReminder = useUploadReminder(uploadSnapshot);

  const [form, setForm] = useState(() => ({
    amount: '', type: 'expense', category: 'Miscellaneous', description: '',
    txn_date: todayISO(), account: '', account_id: '', recurring: false, note: '',
    payment_method: '', // Phase 81.3 — optional; cash|card|upi|bank_transfer|wallet|cheque|other
    promote_global: false, // v3.6.7 Phase 84.28.5 — superadmin founder confirmation toggle
  }));

  // ---------------------------------------------------------------------------
  // v2.10.2 — Draft persistence for the Edit dialog.
  //
  // Failure mode we're guarding against: user is mid-edit (Dialog open, form
  // populated), auth session silently expires (default Supabase lifetime is
  // ~1 hour), refresh fails, AuthContext emits SIGNED_OUT, ProtectedRoute
  // yanks the user to /login. Component unmounts → form + editingId + open
  // state all vanish → careful edits are lost.
  //
  // Guard: every time the form changes while the dialog is open on an EXISTING
  // row (editingId set), snapshot { editingId, form, savedAt } to localStorage
  // keyed by user_id. On next mount, if a draft is found we show a Sonner
  // toast with Resume / Discard actions so the operator chooses what to do.
  // Save success and explicit Cancel both clear the draft.
  // ---------------------------------------------------------------------------
  const _draftKey = useCallback(
    () => (user?.id ? `transactions.editDraft.${user.id}` : null),
    [user?.id],
  );

  const clearDraft = useCallback(() => {
    const k = _draftKey();
    if (!k) return;
    try { localStorage.removeItem(k); } catch { /* ignore */ }
  }, [_draftKey]);

  // Persist on every form change while editing an existing row.
  useEffect(() => {
    const k = _draftKey();
    if (!k || !open || !editingId) return;
    try {
      localStorage.setItem(k, JSON.stringify({
        editingId,
        form,
        savedAt: new Date().toISOString(),
      }));
    } catch { /* quota / privacy mode — ignore silently */ }
  }, [_draftKey, open, editingId, form]);

  // Restore prompt on mount — checks for a saved draft belonging to this
  // user and offers to resume. Runs once per user_id so re-focus doesn't
  // spam the toast.
  const _draftRestorePromptedRef = useRef(false);
  useEffect(() => {
    const k = _draftKey();
    if (!k || _draftRestorePromptedRef.current) return;
    let raw = null;
    try { raw = localStorage.getItem(k); } catch { return; }
    if (!raw) return;
    let d = null;
    try { d = JSON.parse(raw); } catch { clearDraft(); return; }
    if (!d?.editingId || !d?.form) { clearDraft(); return; }
    _draftRestorePromptedRef.current = true;
    const savedAgo = d.savedAt
      ? Math.max(0, Math.round((Date.now() - new Date(d.savedAt).getTime()) / 60000))
      : null;
    toast.info(
      'You had an unsaved edit.',
      {
        description: savedAgo != null
          ? `Draft saved ${savedAgo === 0 ? 'just now' : `${savedAgo} min ago`}. Resume where you left off?`
          : 'Resume where you left off?',
        duration: 12000,
        action: {
          label: 'Resume',
          onClick: () => {
            setEditingId(d.editingId);
            setForm(d.form);
            setOpen(true);
          },
        },
        cancel: {
          label: 'Discard',
          onClick: () => clearDraft(),
        },
      },
    );
  }, [_draftKey, clearDraft]);

  // Reset category when type changes
  useEffect(() => {
    const cats = categoriesForType(form.type);
    if (!cats.includes(form.category)) {
      setForm((f) => ({ ...f, category: cats[cats.length - 1] }));
    }
  }, [form.type]); // eslint-disable-line

  // Deep-link: mobile bottom-nav "Add transaction" navigates to
  // /transactions?new=1 (see components/mobile/MobileActionSheet.js).
  // v3.4.6 — dashboard quick-log chips add prefill params
  // (?new=1&desc=X&amount=Y&category=Z&type=expense) to skip 5 fields
  // of typing. Read them here, hydrate the form, then strip all params
  // so refresh doesn't re-open with stale prefills.
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      const prefillDesc = searchParams.get('desc') || '';
      const prefillAmount = searchParams.get('amount') || '';
      const prefillCategory = searchParams.get('category') || 'Miscellaneous';
      const prefillType = searchParams.get('type') || 'expense';
      // Phase 86.K — "Record transaction" on a cash/wallet row lands here with
      // the account already chosen.
      const prefillAccountId = searchParams.get('account_id') || '';
      const prefillAccount = searchParams.get('account') || '';
      setEditingId(null);
      setForm({
        amount: prefillAmount, type: prefillType, category: prefillCategory,
        description: prefillDesc, txn_date: todayISO(),
        account: prefillAccount, account_id: prefillAccountId, recurring: false, note: '',
        payment_method: '',
      });
      setOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      next.delete('desc');
      next.delete('amount');
      next.delete('category');
      next.delete('type');
      next.delete('account_id');
      next.delete('account');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // v3.4.6 — Fetch top merchants for the description autocomplete.
  // Runs once when the session becomes available AND whenever the Add
  // dialog opens (so the list reflects recent activity if the user
  // logged something and came back). Fed into a native <datalist>
  // attached to the description input inside the dialog.
  useEffect(() => {
    if (!session?.access_token) return () => {};
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/quick-log/top-merchants`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
          params: { days: 90, limit: 20 },
        });
        if (!cancelled && Array.isArray(res.data)) setDescriptionSuggestions(res.data);
      } catch (_e) {
        // Silent — autocomplete is opportunistic, form still works without.
      }
    })();
    return () => { cancelled = true; };
  }, [session?.access_token, open]);

  // Phase 84.20 (Beta issue #9) — Deep-link filters from Insights.
  // Insights' "See all categories" links each category row to this page
  // with query params:
  //   • category  (canonical label, e.g. "Food & Dining")
  //   • month     (YYYY-MM, applies as a custom date range for the month)
  //   • from,to   (explicit YYYY-MM-DD range, overrides `month` if both set)
  //   • type      (optional; defaults to 'expense' when category is set)
  // We consume params once on mount, set state, then strip them from
  // the URL so a subsequent refresh does not overwrite user filter edits.
  const _drilldownAppliedRef = useRef(false);
  useEffect(() => {
    if (_drilldownAppliedRef.current) return;
    // v3.4.6 — skip drilldown parsing when the URL carries the
    // /transactions?new=1&...&category=...&type=... shape used by the
    // dashboard "Quick log" chips. Those params are meant for the Add
    // dialog prefill (see the searchParams effect above), NOT for the
    // Insights filter bar. Without this guard, the drilldown effect
    // races the prefill effect for the same params and strips them
    // before the second prefill run can read them.
    if (searchParams.get('new') === '1') return;
    const qCategory = searchParams.get('category');
    const qMonth    = searchParams.get('month');
    const qFrom     = searchParams.get('from');
    const qTo       = searchParams.get('to');
    const qType     = searchParams.get('type');
    // Phase 86.C — the shared Spending Explorer drills through by merchant and
    // by account too, not just category, so those arrive as filters as well.
    const qSearch   = searchParams.get('q');
    const qAccount  = searchParams.get('account');
    if (!qCategory && !qMonth && !qFrom && !qTo && !qType && !qSearch && !qAccount) return;
    _drilldownAppliedRef.current = true;
    if (qCategory) setFilterCat(qCategory);
    if (qSearch)  setSearch(qSearch);
    if (qAccount) setFilterAccountId(qAccount);
    if (qType && ['all', 'expense', 'income', 'investment', 'transfer', 'refund'].includes(qType)) {
      setFilterType(qType);
    } else if (qCategory || qSearch) {
      // Default to expense when a category is passed but no type — the
      // canonical taxonomy makes expense the most common drill-down.
      setFilterType('expense');
    }
    // Date range: prefer explicit from/to; fall back to month.
    if (qFrom || qTo) {
      setRangePreset('custom');
      if (qFrom) setCustomStart(qFrom);
      if (qTo)   setCustomEnd(qTo);
    } else if (qMonth && /^\d{4}-\d{2}$/.test(qMonth)) {
      const [yy, mm] = qMonth.split('-').map(Number);
      const first = new Date(yy, mm - 1, 1);
      const last  = new Date(yy, mm, 0);
      const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      setRangePreset('custom');
      setCustomStart(isoDay(first));
      setCustomEnd(isoDay(last));
    }
    // Strip the drill-down params so a page refresh does not re-apply
    // (which would clobber any subsequent user edits to the filters).
    const next = new URLSearchParams(searchParams);
    ['category', 'month', 'from', 'to', 'type', 'q', 'account'].forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const load = async ({ silent = false } = {}) => {
    // Only show the spinner on the very first cold load. Refocus refetches
    // and post-mutation reloads must NEVER blank the existing rows, that's
    // the Safari-tab-return UX bug we're fixing here.
    if (!silent && !hasLoadedOnceRef.current) setLoading(true);
    try {
      const [txRes, acctRes] = await Promise.all([
        // Phase 84.19 — Bug 3 fix. Historically this fetched .limit(500),
        // which silently dropped anything older than the 500 most-recent
        // rows for users with heavy import history (beta user was seeing
        // April 23 onwards only). PostgREST caps individual responses at
        // 1000 rows via db-max-rows regardless of what `.limit()` we pass,
        // so we paginate with `.range()` in a loop. Same pattern as
        // admin_dedupe._build_dedupe_plan_for_user on the backend.
        withTimeout(
          fetchAllTransactions(),
          20000, 'transactions.txns',
        ),
        withTimeout(supabase.from('accounts').select('*').order('name'), 12000, 'transactions.accounts'),
      ]);
      // Best-effort fetch of merchant rules. Failure here must not block
      // the main load, manual auto-categorization falls back to the
      // keyword library when rules are unavailable.
      try {
        const rulesRes = await withTimeout(
          supabase.from('merchant_rules').select('pattern, category').limit(500),
          8000, 'transactions.merchant_rules',
        );
        if (Array.isArray(rulesRes?.data)) {
          const sorted = [...rulesRes.data].sort(
            (a, b) => (b.pattern?.length || 0) - (a.pattern?.length || 0),
          );
          setMerchantRules(sorted);
        }
      } catch { /* silent; rules are a nice-to-have */ }
      // Only commit successful, non-error responses. A failed fetch leaves
      // the previously cached/state data on screen.
      if (!txRes?.error && Array.isArray(txRes?.data)) {
        setTxns(txRes.data);
      } else if (txRes?.error) {
        toast.error(txRes.error.message);
      }
      if (Array.isArray(acctRes?.data)) {
        setAccounts(acctRes.data);
      }
      if (user?.id && Array.isArray(txRes?.data)) {
        writeCache(user.id, 'transactions:v1', {
          txns: txRes.data,
          accounts: Array.isArray(acctRes?.data) ? acctRes.data : accounts,
          merchantRules,
        });
      }
      hasLoadedOnceRef.current = true;
    } catch (e) {
      // Timeout / network failure → keep prior data visible.
      console.warn('Transactions load failed (keeping prior data):', e?.message || e);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Initial load + new-user load. We depend on `user?.id` (a stable string)
  // not the `user` object reference, so Supabase TOKEN_REFRESHED events
  // (which create a fresh user object every ~hour and on tab refocus) don't
  // re-fire this and blank the UI.
  useEffect(() => { if (user?.id) load(); /* eslint-disable-next-line */ }, [user?.id]);

  // After a long Safari background (≥60s), the JS heap may have been purged.
  // When the tab returns to focus, silently refetch, no skeleton, no flash,
  // and on failure the cached/hydrated data simply stays as it is.
  useFocusRefetch(() => load({ silent: true }), { minHiddenMs: 60_000 });

  const dateRange = useMemo(
    () => resolveRange(rangePreset, customStart, customEnd),
    [rangePreset, customStart, customEnd],
  );

  // Phase 82.9 — Resolve `t.account_id` to the account's current name.
  // Legacy `t.account` (text) is a snapshot from when the row was added;
  // statement-imported rows don't populate it at all (RPC only sets
  // account_id). This lookup gives us a consistent display + search source
  // regardless of how the transaction was created, and picks up account
  // renames automatically.
  const accountsById = useMemo(() => {
    const m = new Map();
    (accounts || []).forEach((a) => m.set(a.id, a));
    return m;
  }, [accounts]);

  // Phase 84.22.9 (Wave B2) — use the app-root CurrencyContext so this
  // page uses the same FX rates as Insights, Recurring, and the
  // Dashboard. `accountsById` is still local because per-account UI
  // (name, tone) needs more than currency alone.
  const currencyCtx = useCurrencyContext();
  const amountInBase = useCallback((txn) => {
    const acct = txn.account_id ? accountsById.get(txn.account_id) : null;
    const cur = (acct?.currency || baseCurrency || 'INR').toUpperCase();
    return currencyCtx.toBase(Math.abs(Number(txn.amount) || 0), cur);
  }, [accountsById, baseCurrency, currencyCtx]);
  const displayAccount = (t) => {
    if (t.account_id && accountsById.has(t.account_id)) {
      return accountsById.get(t.account_id).name;
    }
    return t.account || '';
  };

  // v3.19.4 — one place to drop every chip-able filter. The date range is
  // intentionally left alone: it is a separate, always-visible control.
  const clearAllFilters = useCallback(() => {
    setSearch('');
    setFilterType('all');
    setFilterCat('all');
    setFilterAccountId('all');
  }, []);

  const filtered = useMemo(() => {
    const { from, to } = dateRange;
    const rows = txns.filter((t) => {
      if (filterType !== 'all' && t.type !== filterType) return false;
      if (filterCat !== 'all' && (t.category || 'Miscellaneous') !== filterCat) return false;
      // Phase 51.2, account-scoped view. Matches on the canonical
      // `account_id` FK. Legacy rows without account_id are excluded when
      // a specific account is selected (they appear under "All accounts").
      if (filterAccountId !== 'all' && t.account_id !== filterAccountId) return false;
      // Date range, both bounds inclusive. Empty `from`/`to` = unbounded.
      if (!txnInRange(t.txn_date, from, to)) return false;
      if (search) {
        const q = search.toLowerCase();
        // Match against the raw statement text (which already contains
        // UPI/IMPS/NEFT/ATW tokens) plus category + resolved account name.
        // Resolving via `displayAccount` means the search finds
        // statement-imported rows by their account name too.
        //
        // Phase 84.20.5 — Notes are now part of the search haystack.
        // Notes are user-authored context; searching them makes them
        // useful for retrieval, which is their whole point.
        const primaryHay = `${t.description || ''} ${t.category || ''} ${displayAccount(t)}`.toLowerCase();
        const noteHay = (t.note || '').toLowerCase();
        const primaryHit = primaryHay.includes(q);
        const noteHit = !!noteHay && noteHay.includes(q);
        if (!primaryHit && !noteHit) return false;
      }
      return true;
    });

    // Phase 84.20 (Beta issue #6) — Apply the user's sort choice.
    // Note: for non-date sorts we still want a stable secondary key so
    // rows with the same amount don't shuffle randomly on re-render.
    const sorted = [...rows];
    switch (sortMode) {
      case 'date_asc':
        sorted.sort((a, b) => String(a.txn_date || '').localeCompare(String(b.txn_date || '')));
        break;
      case 'amount_desc':
        sorted.sort((a, b) => {
          const av = Math.abs(Number(a.amount) || 0);
          const bv = Math.abs(Number(b.amount) || 0);
          if (av !== bv) return bv - av;
          return String(b.txn_date || '').localeCompare(String(a.txn_date || ''));
        });
        break;
      case 'amount_asc':
        sorted.sort((a, b) => {
          const av = Math.abs(Number(a.amount) || 0);
          const bv = Math.abs(Number(b.amount) || 0);
          if (av !== bv) return av - bv;
          return String(b.txn_date || '').localeCompare(String(a.txn_date || ''));
        });
        break;
      case 'merchant_asc':
        sorted.sort((a, b) => {
          const av = (a.description || '').toLowerCase();
          const bv = (b.description || '').toLowerCase();
          const cmp = av.localeCompare(bv);
          if (cmp !== 0) return cmp;
          return String(b.txn_date || '').localeCompare(String(a.txn_date || ''));
        });
        break;
      case 'date_desc':
      default:
        // The upstream `fetchAllTransactions` already reverses to
        // newest-first, so the natural array order matches this case.
        // We still sort explicitly to keep the invariant even if the
        // upstream helper changes.
        sorted.sort((a, b) => String(b.txn_date || '').localeCompare(String(a.txn_date || '')));
        break;
    }
    return sorted;
    // displayAccount is a stable closure over accountsById which is already
    // in the deps below; adding displayAccount would just churn identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txns, filterType, filterCat, filterAccountId, search, dateRange, accountsById, sortMode]);

  // Phase 84.20.5 — Compute the set of txn ids whose ONLY match was in
  // the note (i.e. neither description, category nor account contained
  // the search query). The row renderer uses this to render a small
  // "📝 note" chip so users understand why an unexpected row surfaced.
  // Empty set when there's no active search — cheap early-out.
  const matchedByNoteIds = useMemo(() => {
    if (!search) return new Set();
    const q = search.toLowerCase();
    const s = new Set();
    for (const t of filtered) {
      const primaryHay = `${t.description || ''} ${t.category || ''} ${displayAccount(t)}`.toLowerCase();
      if (primaryHay.includes(q)) continue;
      const noteHay = (t.note || '').toLowerCase();
      if (noteHay && noteHay.includes(q)) s.add(t.id);
    }
    return s;
    // displayAccount is a stable closure over accountsById (already in deps).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, search, accountsById]);

  // Group filtered transactions by month (YYYY-MM) with per-month subtotals.
  // Sorted newest-first to match the visual flow of the list.
  // Phase 84.22.5 — subtotals are aggregated in BASE currency so a month
  // with a mix of INR / USD / AED spending renders one comparable number.
  const groupedByMonth = useMemo(() => {
    const groups = new Map();
    for (const t of filtered) {
      const k = (t.txn_date || '').slice(0, 7) || 'unknown';
      if (!groups.has(k)) groups.set(k, { key: k, items: [], income: 0, expense: 0, investment: 0, refund: 0 });
      const g = groups.get(k);
      g.items.push(t);
      const baseAmt = amountInBase(t);
      if (t.type === 'income') g.income += baseAmt;
      else if (t.type === 'expense') g.expense += baseAmt;
      else if (t.type === 'investment') g.investment += baseAmt;
      else if (t.type === 'refund') g.refund += baseAmt;
    }
    return Array.from(groups.values()).sort((a, b) => b.key.localeCompare(a.key));
  }, [filtered, amountInBase]);

  // Phase 86.C — native when the filtered set is single-currency, converted
  // only when it genuinely spans currencies.
  //
  // These KPIs are FX-converted so a ₹32,000 EMI + $500 subscription + AED 900
  // rent roll up as one comparable headline. But when EVERY visible row is from
  // one currency — which is exactly what happens after drilling in from the
  // Spending Explorer with an account filter — converting is actively confusing:
  // the explorer said ₹20,000 and this card would answer "AED 769.82" for the
  // same two rows. Both correct, and yet it reads as a contradiction.
  const totalsCurrency = useMemo(() => {
    const curs = new Set(
      filtered.map((x) => {
        const acct = x.account_id ? accountsById.get(x.account_id) : null;
        return (acct?.currency || baseCurrency || 'INR').toUpperCase();
      }),
    );
    return curs.size === 1 ? [...curs][0] : baseCurrency;
  }, [filtered, accountsById, baseCurrency]);

  const totals = useMemo(() => {
    const t = { income: 0, expense: 0, investment: 0, refund: 0, borrowed: 0, unresolved: 0 };
    const native = totalsCurrency !== baseCurrency;
    filtered.forEach((x) => {
      if (t[x.type] == null) return;
      const amount = native ? Math.abs(Number(x.amount) || 0) : amountInBase(x);
      // BUG-002 — borrowed money is cash in, never income. And a credit with
      // no evidence of being income is cash received awaiting confirmation.
      // Same shared rule the Dashboard, Insights and the recap use.
      if (x.type === 'income' && !isRealIncome(x)) {
        if (isUnresolvedInflow(x)) t.unresolved += amount;
        else t.borrowed += amount;
        return;
      }
      t[x.type] += amount;
    });
    return t;
  }, [filtered, amountInBase, totalsCurrency, baseCurrency]);

  // -------------------------------------------------------------------------
  // Phase 3 — Transactions Redesign
  // -------------------------------------------------------------------------
  // Day-grouped view (replaces the month grouping in the render layer).
  // Phase 84.20 (Beta #6 follow-up) — Day headers are only meaningful when
  // the primary sort is by date. For amount / merchant sorts we render a
  // flat list so the sort actually spans across days (e.g. "Blinkit High
  // → Low" surfaces the biggest Blinkit charge regardless of month).
  //
  // Phase 84.20.4 — "Oldest first" fix. `groupByDay` unconditionally
  // returns groups sorted DESC by key (newest first). Reverse the array
  // when the user picked "Oldest first" so the day headers actually
  // start from the earliest date on top.
  const isDateSort = sortMode === 'date_desc' || sortMode === 'date_asc';
  const groupedByDay = useMemo(() => {
    const groups = groupByDay(filtered, amountInBase);
    return sortMode === 'date_asc' ? [...groups].reverse() : groups;
  }, [filtered, sortMode, amountInBase]);

  // Right-side detail drawer for viewing / quick-editing a single txn.
  // `drawerTxn` is the currently open transaction (or null). Clicking a
  // row sets it; the drawer's Save button calls `save()` which reuses the
  // legacy edit path (via the existing form state + openForEdit).
  const [drawerTxn, setDrawerTxn] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Bulk-selection model. Set<txnId>. Selection mode is implicit — any
  // count > 0 flips it on.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const selectionMode = selectedIds.size > 0;
  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // Reconciliation candidate count for the Needs Your Attention band.
  // We only need the count — the deep-review UI already lives on the
  // dedicated ReconciliationBanner above.
  const [reconcileCount, setReconcileCount] = useState(0);
  useEffect(() => {
    if (!session?.access_token) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/reconciliation/pending`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (!cancelled) setReconcileCount(res?.data?.count || (res?.data?.candidates || []).length || 0);
      } catch { /* non-fatal; band just hides the reconciliation row */ }
    })();
    return () => { cancelled = true; };
  }, [session?.access_token]);

  // Low-confidence categorisations for the Attention band. Derived
  // client-side from the currently filtered list so it always matches
  // what the user is actually looking at.
  const lowConfidenceTxns = useMemo(
    () => (filtered || []).filter((t) => confidenceOf(t).key === 'low').slice(0, 50),
    [filtered],
  );

  // Credits that arrived with no evidence of being income. Cash received,
  // meaning unknown — kept out of income and surfaced for confirmation.
  const unresolvedInflowTxns = useMemo(
    () => (filtered || []).filter(isUnresolvedInflow).slice(0, 50),
    [filtered],
  );

  // Recurring options for the "Link to recurring" pickers (drawer + bulk).
  // Fetched once on mount; cheap enough to keep in-memory.
  const [recurringOptions, setRecurringOptions] = useState([]);
  useEffect(() => {
    if (!user?.id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('recurring_expenses')
          .select('id, name, frequency, next_due, amount, category, kind')
          .eq('user_id', user.id)
          .eq('active', true)
          .order('name');
        if (!cancelled) setRecurringOptions(data || []);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  // The recurring commitment matched to the current drawer txn (if any).
  const matchedRecurringForDrawer = useMemo(() => {
    if (!drawerTxn) return null;
    const rid = drawerTxn.recurring_id;
    if (!rid) return null;
    return recurringOptions.find((r) => r.id === rid) || null;
  }, [drawerTxn, recurringOptions]);

  // BUG-001 — one Undo for every correction path. A correction is only
  // undone when the rows go back to exactly what they were AND the personal
  // rule it created stops applying to the next import.
  const undoCorrection = useCallback(async (envelope, revertedFrom) => {
    if (!session?.access_token) return;
    const rowCount = (envelope?.previous || envelope?.transaction_ids || []).length || 1;
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/transactions/recategorize-undo`,
        {
          transaction_ids: envelope?.transaction_ids || [],
          previous: envelope?.previous || undefined,
          rule: envelope?.rule || undefined,
          reverted_from_category: revertedFrom,
        },
        { headers: { Authorization: `Bearer ${session.access_token}` }, timeout: 8000 },
      );
      const ruleGone = res?.data?.rule_removed || res?.data?.rule_restored;
      toast.success(
        `Reverted ${rowCount} row${rowCount === 1 ? '' : 's'}.`,
        ruleGone
          ? { description: "We also stopped applying this to future imports." }
          : undefined,
      );
      load({ silent: true });
    } catch {
      toast.error('Could not undo. Please retry.');
    }
  }, [session?.access_token, load]);

  // Row click handler — opens the drawer instead of the legacy modal.
  const openDrawerFor = useCallback((txn) => {
    setDrawerTxn(txn);
    setDrawerOpen(true);
  }, []);

  // Drawer-side quick-save. Patches Supabase directly and refreshes.
  // Kept lean — the create modal still owns the heavier flows
  // (receipt scan / promote-to-recurring / merchant-signal logging).
  const saveFromDrawer = useCallback(async (draft) => {
    if (!user?.id || !draft?.id) return;
    // Preserve sign convention: expense/investment/transfer are stored
    // signed-negative on disk. Match the existing convention in save().
    const magnitude = Math.abs(Number(draft.amount) || 0);
    const signedAmount = ['expense', 'investment', 'transfer'].includes(draft.type)
      ? -magnitude
      : magnitude;

    // Phase 84.9 — Category propagation restored on the drawer save path.
    //
    // Original behaviour before this fix: this callback wrote the entire
    // patch (including `category`) via a single Supabase update on the
    // target row only. The category-propagation endpoint
    // `/api/transactions/{id}/recategorize-and-propagate` (which fans a
    // user-confirmed category out to similar merchant rows, creates a
    // user rule, and casts a global vote) was never invoked. So a user
    // who edited "Ernakulam Medicalcen" from `health_medical` to
    // `Medical` saw exactly one row change — not the sibling rows on
    // the same merchant. That was the "wow" gap Phase 70M was meant to
    // close.
    //
    // Fix: when the category changed, split the write into two steps —
    // (a) Supabase update for non-category fields (amount, type, desc,
    //     date, account, note, recurring), and
    // (b) POST to /api/transactions/{id}/recategorize-and-propagate for
    //     the category field, which handles the target row update AND
    //     the sibling propagation in one atomic hop.
    // When only non-category fields changed we take the fast path (no
    // extra network hop).
    //
    // The endpoint already respects safety rails: never overwrites
    // rows that were user_confirmed / user_propagated / user_skipped /
    // learned. Caps at 500 rows.
    const originalCategory = drawerTxn?.category || null;
    const categoryChanged = originalCategory !== draft.category;

    // Step (a): Non-category fields via direct Supabase.
    const nonCatPatch = {
      amount: signedAmount,
      type: draft.type,
      description: draft.description,
      txn_date: draft.txn_date,
      account_id: draft.account_id || null,
      note: draft.note || null,
      recurring: !!draft.recurring,
    };
    // If category did NOT change, we also include the current category
    // and category_source in the same patch to preserve the pre-fix
    // "user-edited => manual" contract. If it DID change, we skip
    // category here — the propagate endpoint handles it and stamps
    // `category_source='user_confirmed'` for us.
    if (!categoryChanged) {
      nonCatPatch.category = draft.category;
      nonCatPatch.category_source = 'manual';
    }

    const { error } = await supabase
      .from('transactions')
      .update(nonCatPatch)
      .eq('id', draft.id);
    if (error) { toast.error(error.message || 'Could not save.'); return; }

    // Step (b): Category propagation (only when the category changed).
    // Phase 84.20 (Beta issues #7 & #8) — user-locked policy:
    // "Ask user via toast; apply to all past + future for that merchant."
    //
    // Two-step flow:
    //   1) Save target row's category only (fast, always applied).
    //   2) Preview the # of similar rows that WOULD be touched.
    //   3) Show a confirm toast — user clicks "Apply" or dismisses.
    //   4) If "Apply", call recategorize-and-propagate (also creates
    //      merchant_rules so future imports pick it up).
    if (categoryChanged && draft.category && session?.access_token) {
      // (1) Save the target's category via a plain update so the user's
      // primary edit lands even if the network hop below fails.
      try {
        await supabase
          .from('transactions')
          .update({ category: draft.category, category_source: 'user_confirmed' })
          .eq('id', draft.id);
      } catch { /* best-effort */ }

      // (2) Preview similar count.
      let previewCount = 0;
      let merchantDisplay = '';
      try {
        const previewResp = await axios.post(
          `${BACKEND_URL}/api/transactions/${draft.id}/preview-similar`,
          { category: draft.category },
          { headers: { Authorization: `Bearer ${session.access_token}` }, timeout: 6000 },
        );
        previewCount = previewResp.data?.count || 0;
        merchantDisplay = previewResp.data?.merchant_display || '';
      } catch (e) {
        // Preview failed — fall back to the legacy auto-propagate
        // behaviour so we still learn. The user just doesn't see the
        // confirmation toast.
        try {
          const resp = await axios.post(
            `${BACKEND_URL}/api/transactions/${draft.id}/recategorize-and-propagate`,
            {
              category: draft.category,
              also_create_rule: true,
              previous_category: originalCategory,
              previous_category_source: drawerTxn?.category_source || null,
              previous_category_confidence: drawerTxn?.category_confidence ?? null,
            },
            { headers: { Authorization: `Bearer ${session.access_token}` } },
          );
          const n = resp.data?.also_updated_count || 0;
          const env = resp.data?.undo || null;
          const undoAction = env
            ? { label: 'Undo', onClick: () => undoCorrection(env, draft.category) }
            : undefined;
          toast.success(
            n > 0 ? `Updated. Also applied to ${n} similar transaction${n === 1 ? '' : 's'}.`
                  : 'Transaction updated.',
            { duration: 8000, action: undoAction },
          );
        } catch (e2) {
          toast.success('Transaction updated.');
        }
        setDrawerOpen(false);
        setDrawerTxn(null);
        load();
        return;
      }

      // (3) Nothing similar to sweep — but the correction still teaches a
      // personal rule, so the user must be able to take that back
      // (BUG-001: an Undo has to be reachable from the primary control).
      if (previewCount <= 0) {
        try {
          const resp = await axios.post(
            `${BACKEND_URL}/api/transactions/${draft.id}/recategorize-and-propagate`,
            {
              category: draft.category,
              also_create_rule: true,
              previous_category: originalCategory,
              previous_category_source: drawerTxn?.category_source || null,
              previous_category_confidence: drawerTxn?.category_confidence ?? null,
            },
            { headers: { Authorization: `Bearer ${session.access_token}` }, timeout: 8000 },
          );
          const env = resp.data?.undo || null;
          toast.success('Transaction updated.', {
            duration: 8000,
            description: env?.rule ? "We'll use this for future imports too." : undefined,
            action: env
              ? { label: 'Undo', onClick: () => undoCorrection(env, draft.category) }
              : undefined,
          });
        } catch {
          toast.success('Transaction updated.');
        }
      } else {
        // (4) Show confirm toast with an Apply action. Non-modal — the
        // toast auto-dismisses if the user does nothing.
        const merchantLabel = merchantDisplay
          ? merchantDisplay.replace(/\s+/g, ' ').slice(0, 28)
          : 'this merchant';
        toast(`Apply "${draft.category}" to ${previewCount} similar transaction${previewCount === 1 ? '' : 's'} from ${merchantLabel}?`, {
          duration: 12000,
          action: {
            label: 'Apply',
            onClick: async () => {
              try {
                const resp = await axios.post(
                  `${BACKEND_URL}/api/transactions/${draft.id}/recategorize-and-propagate`,
                  {
                    category: draft.category,
                    also_create_rule: true,
                    previous_category: originalCategory,
                    previous_category_source: drawerTxn?.category_source || null,
                    previous_category_confidence: drawerTxn?.category_confidence ?? null,
                  },
                  { headers: { Authorization: `Bearer ${session.access_token}` } },
                );
                const n = resp.data?.also_updated_count || 0;
                const env = resp.data?.undo || null;
                toast.success(
                  `Applied to ${n} similar transaction${n === 1 ? '' : 's'}. Future imports will follow suit.`,
                  {
                    duration: 8000,
                    action: env
                      ? { label: 'Undo', onClick: () => undoCorrection(env, draft.category) }
                      : undefined,
                  },
                );
                load();
              } catch (err) {
                toast.error('Could not apply to similar transactions. Please try again.');
              }
            },
          },
        });
      }
    }

    // If category did NOT change, we still need to surface a success
    // toast (the category-changed branch handles its own toast copy).
    if (!categoryChanged) {
      toast.success('Transaction updated.');
    }

    setDrawerOpen(false);
    setDrawerTxn(null);
    load();
  }, [user?.id, load, drawerTxn, session?.access_token, undoCorrection]);

  // Helper function to delete a transaction (used by drawer and legacy modal).
  // Declared here before deleteFromDrawer to avoid temporal dead zone error.
  const remove = async (id) => {
    const { error } = await supabase.from('transactions').delete().eq('id', id);
    if (error) return toast.error(error.message);
    toast.success('Removed.');
    load();
  };

  const deleteFromDrawer = useCallback(async () => {
    if (!drawerTxn?.id) return;
    await remove(drawerTxn.id);
    setDrawerOpen(false);
    setDrawerTxn(null);
  }, [drawerTxn?.id, remove]);

  const markReviewedFromDrawer = useCallback(async () => {
    if (!drawerTxn?.id) return;
    const { error } = await supabase
      .from('transactions')
      .update({ reviewed_at: new Date().toISOString() })
      .eq('id', drawerTxn.id);
    if (error) {
      if (/reviewed_at/i.test(error.message || '')) {
        toast.info('Reviewed markers need a database update. Saved locally.');
      } else {
        toast.error(error.message || 'Could not mark reviewed.');
      }
      return;
    }
    toast.success('Marked reviewed.');
    load();
  }, [drawerTxn?.id, load]);

  const linkRecurringFromDrawer = useCallback(async (recurringId) => {
    if (!drawerTxn?.id || !recurringId) return;
    const { error } = await supabase
      .from('transactions')
      .update({ recurring_id: recurringId, recurring: true })
      .eq('id', drawerTxn.id);
    if (error) {
      toast.error(error.message || 'Could not link.');
      return;
    }
    toast.success('Linked to recurring commitment.');
    setDrawerTxn((cur) => (cur ? { ...cur, recurring_id: recurringId, recurring: true } : cur));
    load();
  }, [drawerTxn?.id, load]);

  const unlinkRecurringFromDrawer = useCallback(async () => {
    if (!drawerTxn?.id) return;
    const { error } = await supabase
      .from('transactions')
      .update({ recurring_id: null })
      .eq('id', drawerTxn.id);
    if (error) { toast.error(error.message || 'Could not unlink.'); return; }
    toast.success('Unlinked.');
    setDrawerTxn((cur) => (cur ? { ...cur, recurring_id: null } : cur));
    load();
  }, [drawerTxn?.id, load]);

  // -------------------------------------------------------------------------
  // Bulk action handlers. Each acts on `selectedIds` and refreshes at the
  // end. All mutations are best-effort per row — one failure doesn't halt
  // the batch, but we do surface the count of failures via toast.
  // -------------------------------------------------------------------------
  const bulkRecategorise = useCallback(async (newCategory) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0 || !newCategory) return;
    const { error } = await supabase
      .from('transactions')
      .update({ category: newCategory, category_source: 'user_confirmed' })
      .in('id', ids);
    if (error) { toast.error(error.message || 'Could not recategorise.'); return; }
    // Phase 84.20 (Beta issues #7 & #8) — Bulk edits must also feed the
    // learning loop. For each unique merchant signature in the selection,
    // upsert a `merchant_rules` row so future imports of the same
    // merchant land in `newCategory` automatically. Fire-and-forget:
    // failure here must not un-do the user's primary edit.
    if (session?.access_token) {
      try {
        const resp = await axios.post(
          `${BACKEND_URL}/api/transactions/bulk-learn-rules`,
          { txn_ids: ids, category: newCategory },
          { headers: { Authorization: `Bearer ${session.access_token}` }, timeout: 8000 },
        );
        const created = resp.data?.rules_created || 0;
        const updated = resp.data?.rules_updated || 0;
        const learned = created + updated;
        if (learned > 0) {
          toast.success(`Moved ${ids.length} to ${newCategory}. Learned ${learned} merchant rule${learned === 1 ? '' : 's'} for future imports.`);
        } else {
          toast.success(`Moved ${ids.length} to ${newCategory}.`);
        }
      } catch (e) {
        console.warn('[bulk-learn-rules] failed:', e?.message);
        toast.success(`Moved ${ids.length} to ${newCategory}.`);
      }
    } else {
      toast.success(`Moved ${ids.length} to ${newCategory}.`);
    }
    clearSelection();
    load();
  }, [selectedIds, clearSelection, load, session?.access_token]);

  const bulkMarkReviewed = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const { error } = await supabase
      .from('transactions')
      .update({ reviewed_at: new Date().toISOString() })
      .in('id', ids);
    if (error) {
      if (/reviewed_at/i.test(error.message || '')) {
        toast.info('Reviewed markers need a database update.');
      } else {
        toast.error(error.message || 'Could not mark reviewed.');
      }
      return;
    }
    toast.success(`Marked ${ids.length} reviewed.`);
    clearSelection();
    load();
  }, [selectedIds, clearSelection, load]);

  const bulkLinkRecurring = useCallback(async (recurringId) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0 || !recurringId) return;
    // Phase 84.20 (Beta issue #11) — route through the backend so failures
    // are observable and ownership is verified. The prior direct-Supabase
    // path silently failed for multi-select users (Netflix + others).
    if (!session?.access_token) {
      toast.error('Please refresh — your session expired.');
      return;
    }
    try {
      const resp = await axios.post(
        `${BACKEND_URL}/api/transactions/bulk-link-recurring`,
        { txn_ids: ids, recurring_id: recurringId },
        { headers: { Authorization: `Bearer ${session.access_token}` }, timeout: 15000 },
      );
      const linked = resp.data?.linked ?? ids.length;
      const skipped = resp.data?.skipped ?? 0;
      if (linked > 0 && skipped === 0) {
        toast.success(`Linked ${linked} to recurring.`);
      } else if (linked > 0 && skipped > 0) {
        toast.success(`Linked ${linked}. Skipped ${skipped} (not yours or already gone).`);
      } else {
        toast.info('Nothing to link — please refresh and try again.');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Could not link.';
      toast.error(typeof msg === 'string' ? msg : 'Could not link.');
      return;
    }
    clearSelection();
    load();
  }, [selectedIds, clearSelection, load, session?.access_token]);

  const bulkDelete = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const { error } = await supabase.from('transactions').delete().in('id', ids);
    if (error) { toast.error(error.message || 'Could not delete.'); return; }
    toast.success(`Deleted ${ids.length}.`);
    clearSelection();
    load();
  }, [selectedIds, clearSelection, load]);

  // Phase 84.20 (Beta issue #4) — Legacy categories were appearing in the
  // filter dropdown because we merged `txns.forEach(t => s.add(t.category))`
  // with the canonical taxonomy. Historical rows carry legacy strings like
  // "Loans", "Rent", "Healthcare", "Transportation" (see the legacy block
  // in `CATEGORY_COLORS`) — those polluted the dropdown for years after
  // the taxonomy was locked. Fix: build the dropdown from the CANONICAL
  // taxonomy only. Rows whose stored category is legacy are still shown
  // in the list (they just render with `colorForCategory` fallback and
  // will be remapped by the next auto-heal sweep) — but they no longer
  // add noise to the filter select.
  //
  // Phase 84.20.4 — Users flagged the flat 60-item list as confusing.
  // Return a GROUPED shape so the render layer can display section
  // headers ("Expense", "Income", …). We keep a de-duped flat map
  // available too for callers that don't care about grouping.
  const categoryGroups = useMemo(() => {
    return [
      { label: 'Expense',    items: categoriesForType('expense') },
      { label: 'Income',     items: categoriesForType('income') },
      { label: 'Investment', items: categoriesForType('investment') },
      { label: 'Transfer',   items: categoriesForType('transfer') },
      { label: 'Refund',     items: categoriesForType('refund') },
    ];
  }, []);
  const allCategories = useMemo(() => {
    const s = new Set();
    categoryGroups.forEach((g) => g.items.forEach((c) => s.add(c)));
    return Array.from(s);
  }, [categoryGroups]);

  const onDescriptionBlur = () => {
    if (!form.description) return;
    const cats = categoriesForType(form.type);
    const fallback = cats[cats.length - 1];
    // Only auto-suggest when the user hasn't manually picked a category
    // (it's still on the type's fallback or empty). Respects user intent.
    if (form.category === fallback || !form.category) {
      // 1) User's saved "Remember for next time?" rules win first. They are
      //    explicit user preferences. Longest-pattern-first.
      const d = form.description.toLowerCase();
      const ruleHit = merchantRules.find(
        (r) => r.pattern && d.includes(String(r.pattern).toLowerCase().trim()),
      );
      if (ruleHit && ruleHit.category && cats.includes(ruleHit.category)) {
        setForm((f) => ({ ...f, category: ruleHit.category }));
        return;
      }
      // 2) Otherwise fall back to the built-in keyword library.
      const guess = autoCategorize(form.description, form.type);
      if (guess && cats.includes(guess)) setForm((f) => ({ ...f, category: guess }));
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({ amount: '', type: 'expense', category: 'Miscellaneous', description: '', txn_date: todayISO(), account: '', account_id: '', recurring: false, note: '', payment_method: '' });
    // Explicit close (Cancel button or backdrop click) → drop the draft
    // snapshot so the resume toast doesn't fire again on the next mount.
    clearDraft();
  };

  // ---------------------------------------------------------------------------
  // Phase 69C — Promote a manual recurring transaction into a recurring_expenses
  // rule, with smart fingerprint dedup so the same logical commitment never
  // gets duplicated.
  //
  // When this fires:
  //   • A manual transaction is saved (create or edit) with `recurring=true`.
  //   • Statement-imported transactions (source != 'manual') are deliberately
  //     skipped — auto-promoting from imports would silently flood the user's
  //     Recurring list. A future iteration will surface those as suggestions.
  //
  // Fingerprint (same user + same kind + similar description + similar amount
  // + created within last 90 days):
  //   • description normalised to lowercase, alphanumeric-only, trimmed
  //   • match if EITHER side contains the other (handles "Netflix sub"
  //     matching "NETFLIX" and similar variations)
  //   • amount within ±10% of the existing rule's amount
  //   • only checks rules created in the last 90 days so a one-off legacy
  //     rule from years ago doesn't suppress a legitimate new one
  //
  // If a match is found we skip silently (no toast — the user already has
  // this rule; flagging it as a "duplicate" would feel scolding).
  //
  // Kind mapping (transaction.type → recurring_expenses.kind):
  //   income     → income      (Phase 69B)
  //   investment → investment
  //   expense    → expense
  //   transfer   → SKIP (transfers are not "commitments" in any direction)
  //   refund     → SKIP (refunds are by definition not recurring)
  // ---------------------------------------------------------------------------
  const promoteToRecurringRule = async ({ description, amount, type, category, txn_date, source = 'manual' }) => {
    try {
      if (!user?.id) return;
      if (source !== 'manual') return;
      const kindMap = { income: 'income', investment: 'investment', expense: 'expense' };
      const kind = kindMap[type];
      if (!kind) return;
      const magnitude = Math.abs(Number(amount) || 0);
      if (!magnitude) return;

      const normalise = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
      const incomingNorm = normalise(description);

      // Fingerprint lookup: same user + same kind, last 90 days.
      const since = new Date(Date.now() - 90 * 24 * 3600 * 1000).toISOString();
      const { data: existing } = await supabase
        .from('recurring_expenses')
        .select('id, name, amount, kind, created_at')
        .eq('user_id', user.id)
        .eq('kind', kind)
        .gte('created_at', since)
        .limit(50);

      const match = (existing || []).find((r) => {
        const ruleNorm = normalise(r.name);
        if (!ruleNorm || !incomingNorm) return false;
        const descSimilar = ruleNorm.includes(incomingNorm) || incomingNorm.includes(ruleNorm);
        if (!descSimilar) return false;
        const ruleAmt = Math.abs(Number(r.amount) || 0);
        if (!ruleAmt) return false;
        const ratio = magnitude / ruleAmt;
        return ratio >= 0.9 && ratio <= 1.1;
      });
      if (match) {
        // Quiet skip — the user already has this rule; no need to nag.
        return;
      }

      // No match — create a new rule with sensible defaults.
      // next_due: ~30 days from the transaction date (so it shows up in the
      // user's near-term timeline without retroactively being marked overdue).
      const baseDate = txn_date ? new Date(txn_date) : new Date();
      const next_due = new Date(baseDate.getTime() + 30 * 24 * 3600 * 1000).toISOString().slice(0, 10);
      const cleanName = (description && description.trim()) || category || 'Recurring item';
      const newRow = {
        user_id: user.id,
        kind,
        name: cleanName.slice(0, 80),
        amount: magnitude,
        category: category || (kind === 'income' ? 'Salary' : 'Subscriptions'),
        frequency: 'monthly',
        next_due,
        active: true,
      };
      const { error } = await supabase.from('recurring_expenses').insert(newRow);
      if (error) {
        // Graceful degradation for Phase 69B-not-yet-applied DBs.
        const m = (error.message || '').toLowerCase();
        if (kind === 'income' && /kind_check|check constraint/.test(m)) {
          toast.info('Recurring income needs a database update. Your transaction was saved.', { duration: 5000 });
          return;
        }
        // Pre-Phase 60A schemas may not have `kind` — retry without it.
        if (/column .*kind.* does not exist/i.test(error.message || '')) {
          const { kind: _drop1, ...legacy } = newRow;
          await supabase.from('recurring_expenses').insert(legacy);
          toast.success('Added to your Recurring list.', { duration: 4000 });
          return;
        }
        console.warn('promoteToRecurringRule failed:', error.message);
        return;
      }
      toast.success('Added to your Recurring list.', {
        duration: 4500,
        action: { label: 'View', onClick: () => navigate('/recurring') },
      });
    } catch (e) {
      // Never let the rule-promotion path block the main txn save flow.
      console.warn('promoteToRecurringRule swallowed error:', e?.message || e);
    }
  };

  const openForEdit = (t) => {
    setEditingId(t.id);
    // Phase 61A — remember the original kind/category/source for the
    // post-save merchant-signal log call AND the auto-propagate flow
    // (Phase 70M). Done at edit-time (not at save-time) because by save
    // time `form` already holds the new values.
    editOriginalsRef.current = {
      before_kind:     t.type            || null,
      before_category: t.category        || null,
      before_source:   t.category_source || null,
    };
    setForm({
      // The form input always shows a positive number. Sign is re-applied
      // on save based on `type` (outflows → negative, inflows → positive).
      // This matches how imported rows are stored on disk (expenses are
      // negative) without forcing the user to type a "-" prefix.
      amount: t.amount != null ? String(Math.abs(Number(t.amount))) : '',
      type: t.type || 'expense',
      category: t.category || 'Miscellaneous',
      description: t.description || '',
      txn_date: t.txn_date || todayISO(),
      account: t.account || '',
      account_id: t.account_id || '',
      recurring: !!t.recurring,
      note: t.note || '',
      payment_method: t.payment_method || '',
    });
    setOpen(true);
  };

  const save = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (saving) return;
    if (!user) { toast.error('Please sign in again.'); return; }
    if (!form.amount || Number(form.amount) <= 0) { toast.error('Please add an amount above 0.'); return; }
    // v3.19.4 — an account is required. Saving without one used to do nothing
    // visible (and, worse, would write a transaction attached to no account —
    // the exact orphan-row class cleaned up in v3.19.2).
    if (!form.account_id) { toast.error('Choose the account this transaction belongs to.'); return; }
    setSaving(true);
    try {
      // Look up the chosen account so we can also save its name into the
      // legacy free-text `account` column (keeps backwards compat for older
      // views that haven't migrated to account_id joins yet).
      const chosenAccount = (accounts || []).find((a) => a.id === form.account_id);
      const accountName = chosenAccount?.name || form.account || null;
      // Sign convention on disk:
      //   • expense / investment / transfer → stored as NEGATIVE
      //   • income / refund                 → stored as POSITIVE
      // The form input is always a positive magnitude; we apply the sign
      // here so manual entries match what the import worker writes.
      const OUTFLOW_TYPES = new Set(['expense', 'investment', 'transfer']);
      const magnitude = Math.abs(Number(form.amount));
      const signedAmount = OUTFLOW_TYPES.has(form.type) ? -magnitude : magnitude;
      const baseRow = {
        amount: signedAmount,
        type: form.type,
        category: form.category || autoCategorize(form.description, form.type) || 'Miscellaneous',
        description: form.description || null,
        txn_date: form.txn_date,
        account: accountName,
        recurring: !!form.recurring,
        note: form.note?.trim() ? form.note.trim().slice(0, 200) : null,
        // Phase 81.3 — optional payment method (secondary metadata, also
        // used by reconciliation to gate cash exclusion). Empty string
        // means "unspecified" and must be stored as NULL for the CHECK
        // constraint to accept it.
        payment_method: form.payment_method || null,
      };
      const row = form.account_id ? { ...baseRow, account_id: form.account_id } : baseRow;
      if (editingId) {
        let { error } = await supabase.from('transactions').update(row).eq('id', editingId);
        // Defensive: strip payment_method if the column doesn't exist yet
        // in this environment (Phase 81.1 migration may not be applied).
        if (error && /payment_method/i.test(error.message || '')) {
          const { payment_method: _pm, ...rowNoPm } = row;
          ({ error } = await supabase.from('transactions').update(rowNoPm).eq('id', editingId));
        }
        if (error && /column .*account_id.* does not exist|PGRST204/i.test(`${error.message} ${error.code || ''}`)) {
          const { payment_method: _pm2, ...bareRow } = baseRow;
          ({ error } = await supabase.from('transactions').update(bareRow).eq('id', editingId));
        }
        if (error) {
          console.error('Transaction update failed:', error);
          toast.error(error.message || 'Could not update transaction.');
          return;
        }
        const beforeOrig = editOriginalsRef.current || {};
        const categoryChanged = beforeOrig.before_category !== baseRow.category;

        // Phase 70M — Auto-propagate on edit. When the user changes the
        // category on a single row, sweep their other un-touched rows
        // with the same merchant signature so the ledger feels alive.
        // Falls back gracefully (existing toast) on any failure.
        let propagatePromise = null;
        if (categoryChanged && session?.access_token) {
          propagatePromise = axios.post(
            `${BACKEND_URL}/api/transactions/${editingId}/recategorize-and-propagate`,
            {
              category: baseRow.category,
              also_create_rule: true,
              // BUG-001 — the row above was already written with the new
              // category, so the backend cannot see what it used to be.
              previous_category: beforeOrig.before_category,
              previous_category_source: beforeOrig.before_source,
            },
            {
              headers: { Authorization: `Bearer ${session.access_token}` },
              timeout: 6000,
            },
          ).then((r) => r?.data || null).catch(() => null);
        }

        // Show the calm baseline toast immediately so the user gets
        // instant feedback. We'll upgrade it once propagate returns.
        const toastId = toast.success('Updated.');
        if (propagatePromise) {
          propagatePromise.then((data) => {
            const count = data?.also_updated_count || 0;
            // BUG-001 — an Undo must also retire the personal rule this
            // correction wrote, so we hand the whole undo envelope back.
            const undoEnvelope = data?.undo || null;
            if (count === 0 && undoEnvelope?.rule) {
              // No similar rows, but we did learn a rule — the user must
              // still be able to take that back.
              toast.success('Updated.', {
                id: toastId,
                description: "We'll use this for future imports too.",
                duration: 8000,
                action: {
                  label: 'Undo',
                  onClick: () => undoCorrection(undoEnvelope, baseRow.category),
                },
              });
            }
            if (count > 0) {
              const ids = data?.also_updated_ids || [];
              toast.success(
                `Also applied to ${count} similar transaction${count === 1 ? '' : 's'}.`,
                {
                  id: toastId,
                  description: data?.capped
                    ? `Capped at ${count} — re-edit any row to extend.`
                    : 'Same merchant signature across your ledger.',
                  duration: 8000,
                  action: {
                    label: 'Undo',
                    onClick: () => undoCorrection(undoEnvelope, baseRow.category),
                  },
                },
              );
              // Refresh the list so the propagated rows reflect the new
              // category immediately (instead of waiting for the next
              // navigation).
              load({ silent: true });
            }
          });
        }

        // Phase 61A — fire-and-forget passive signal log. Only "high-signal"
        // edits (investment / debt / transfer-flavour, or Misc→anything per
        // Phase 70M) are actually persisted server-side; the rest return
        // logged:false. Failures here MUST NOT block the user — we
        // deliberately swallow errors.
        try {
          if (
            session?.access_token && (
              beforeOrig.before_kind     !== baseRow.type ||
              beforeOrig.before_category !== baseRow.category
            )
          ) {
            axios.post(
              `${BACKEND_URL}/api/transactions/${editingId}/log-signal`,
              {
                before_kind:     beforeOrig.before_kind,
                after_kind:      baseRow.type,
                before_category: beforeOrig.before_category,
                after_category:  baseRow.category,
                before_source:   beforeOrig.before_source,
                // v3.6.7 Phase 84.28.5 — SuperAdmin founder promotion.
                // Backend ignores this flag for non-superadmin users.
                promote_global:  !!form.promote_global,
              },
              {
                headers: { Authorization: `Bearer ${session.access_token}` },
                timeout: 4000,
              },
            ).catch(() => { /* swallowed by design */ });
          }
        } catch { /* never block the edit on signal logging */ }
      } else {
        // Phase 59 — idempotent manual add. Double-clicking Save or
        // two tabs hitting Submit at once no longer creates duplicates.
        const insertRow = { ...row, user_id: user.id, source: 'manual' };
        const { error, duplicate } = await insertTransactionIdempotent(insertRow);
        if (error) {
          console.error('Transaction insert failed:', error);
          toast.error(error.message || 'Could not save transaction.');
          return;
        }
        if (duplicate) {
          toast.info('A matching transaction already exists in your ledger, so we kept the original.');
        } else {
          toast.success("Saved. You're building clarity one step at a time.");
        }
      }
      // Phase 69C — promote manual recurring transactions into a recurring
      // rule (with smart fingerprint dedup so existing rules aren't
      // duplicated). Fire-and-forget so it never blocks the save flow.
      if (form.recurring) {
        promoteToRecurringRule({
          description: baseRow.description,
          amount: baseRow.amount,
          type: baseRow.type,
          category: baseRow.category,
          txn_date: baseRow.txn_date,
          source: 'manual',
        });
      }
      setOpen(false);
      resetForm();
      clearDraft();
      load();
    } catch (err) {
      console.error('Save crashed:', err);
      toast.error(err?.message || 'Something went wrong while saving.');
    } finally {
      setSaving(false);
    }
  };

  // Phase 81.3 — Lazy Cash Wallet creation. Triggered from inline button in
  // the Add Transaction dialog when the user chooses payment_method=cash
  // and no `accounts.type='cash'` row exists yet. The wallet is created,
  // auto-selected into the current form, and added to the local accounts
  // list so `load()` doesn't need to be awaited before the user continues.
  //
  // Design decision (locked by product): NEVER auto-create a Cash Wallet
  // for every user on signup — it adds clutter for users who don't use
  // cash. Users opt in only when they type a cash transaction.
  const createCashWallet = async () => {
    if (!user || creatingCashWallet) return;
    // Never inherit a currency silently — not from the profile, not from the
    // country, and above all not from whichever account happens to be first in
    // the list, which is how one mislabelled account teaches its mistake to the
    // next. Propose, show where the proposal came from, and let the person say.
    const suggestion = suggestCurrency(profile);
    setWalletProposal({ code: suggestion.code || '', from: suggestion.from });
  };

  const confirmCashWallet = async (currency) => {
    if (!user || creatingCashWallet || !currency) return;
    setCreatingCashWallet(true);
    try {
      const { data, error } = await supabase
        .from('accounts')
        .insert({
          user_id: user.id,
          name: 'Cash Wallet',
          type: 'cash',
          currency,
          balance: 0,
        })
        .select()
        .single();
      if (error) {
        console.error('Cash wallet insert failed:', error);
        toast.error(error.message || 'Could not create Cash Wallet.');
        return;
      }
      // Optimistic update: prepend to accounts list + auto-select.
      setAccounts((prev) => [data, ...(prev || [])]);
      setForm((f) => ({ ...f, account_id: data.id, account: data.name }));
      toast.success('Cash Wallet created. You can rename it later from Accounts.');
      setWalletProposal(null);
    } catch (err) {
      console.error('createCashWallet crashed:', err);
      toast.error(err?.message || 'Could not create Cash Wallet.');
    } finally {
      setCreatingCashWallet(false);
    }
  };

  const handleReceiptUpload = async (file) => {
    if (!file || !session?.access_token) return;
    setScanning(true);
    try {
      const reader = new FileReader();
      const dataUrl = await new Promise((res, rej) => {
        reader.onload = () => res(reader.result);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      const base64 = String(dataUrl).split(',')[1];
      const res = await axios.post(`${BACKEND_URL}/api/receipts/parse`, {
        image_base64: base64,
        mime_type: file.type || 'image/jpeg',
      }, { headers: { Authorization: `Bearer ${session.access_token}` }, timeout: 90000 });
      const r = res.data || {};
      let guessedType = r.type || 'expense';
      // Phase 29, refund detection. If the merchant/description clearly
      // looks like a refund/cashback/reversal, coerce type='refund' so it
      // doesn't pollute Income. Belt-and-suspenders against the LLM
      // mistakenly classifying an incoming-money receipt as 'income'.
      const refundyText = `${r.merchant || ''} ${r.note_to_user || ''}`;
      if (isRefundDescription(refundyText) || (guessedType === 'income' && isRefundDescription(r.merchant || ''))) {
        guessedType = 'refund';
      }
      const cats = categoriesForType(guessedType);
      const cat = cats.includes(r.category) ? r.category : autoCategorize(r.merchant || '', guessedType);
      // Phase B, receipts are usually paid in cash. Pre-select the user's
      // Cash account when present, else the most-recently-used account from
      // their existing transactions, else leave unset for the user to pick.
      const cashAccount = (accounts || []).find((a) => a.type === 'cash');
      const lastUsedId = (() => {
        const recent = txns.find((t) => t.account_id);
        return recent?.account_id || '';
      })();
      const presetAcctId = form.account_id || cashAccount?.id || lastUsedId || '';
      setForm((f) => ({
        ...f,
        amount: r.amount ? String(r.amount) : f.amount,
        type: guessedType,
        category: cat || f.category,
        description: r.merchant || f.description,
        txn_date: r.date || f.txn_date,
        account_id: presetAcctId,
      }));
      if (r.needs_review) {
        toast.warning(r.note_to_user || 'Some details were hard to read — please review before saving.', { duration: 7000 });
      } else if (presetAcctId && cashAccount && presetAcctId === cashAccount.id) {
        toast.success('Receipt scanned. Saved to your Cash account — change if needed.');
      } else {
        toast.success('Receipt scanned. Review the details before saving.');
      }
    } catch (err) {
      console.error('Receipt scan failed', err);
      toast.error(err.response?.data?.detail || 'Could not read that receipt. Try a clearer image.');
    } finally {
      setScanning(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-5">
      {/* Phase 70I.5 — One-time post-remap banner (renders nothing if not active) */}
      <CategoryRemapNoticeBanner />

      {/* Phase 80 — Upload reminder banner. Slim single-row nudge that
          fires on stale-14d / month-end / new-month. Not dismissible;
          disappears naturally once fresh data lands. */}
      {uploadReminder && <UploadReminderBanner reminder={uploadReminder} />}

      {/* Phase 81.3 — Manual↔statement reconciliation review tray.
          Renders nothing (no chrome, no spinner) when count === 0, so
          zero visual impact for users without any pending matches. */}
      <ReconciliationBanner session={session} onResolved={load} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">Transactions</h1>
          <p className="text-sm text-muted-foreground">Add, edit, and explore the flow of your money.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/import"><Button variant="secondary" className="rounded-xl" data-testid="go-import-button"><Upload className="h-4 w-4 mr-1.5" /> Import Statements</Button></Link>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="rounded-xl" data-testid="transaction-add-button"><Plus className="h-4 w-4 mr-1" /> Add transaction</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg rounded-2xl">
              <DialogHeader><DialogTitle className="font-display">{editingId ? 'Edit transaction' : 'New transaction'}</DialogTitle></DialogHeader>
              <form onSubmit={save} className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  {TYPES.map((t) => (
                    <button key={t.value} type="button" onClick={() => setForm({ ...form, type: t.value })} data-testid={`tx-type-${t.value}`} className={`rounded-xl border px-3 py-2.5 text-sm flex items-center justify-center gap-2 transition-colors ${form.type === t.value ? 'border-primary bg-accent text-accent-foreground' : 'border-border/70 hover:border-border bg-card'}`}>
                      <t.icon className="h-4 w-4" /> {t.label}
                    </button>
                  ))}
                </div>

                <div className="rounded-xl border border-dashed border-border/70 bg-accent/20 px-3 py-2 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <ScanLine className="h-4 w-4 text-primary shrink-0" />
                    <div className="min-w-0">
                      <div className="text-xs font-medium">Scan a receipt</div>
                      <div className="text-[11px] text-muted-foreground truncate">PDF or image. We&rsquo;ll auto-fill amount, merchant and date.</div>
                    </div>
                  </div>
                  <input ref={fileRef} type="file" accept="image/*,application/pdf,.pdf" className="hidden" onChange={(e) => handleReceiptUpload(e.target.files?.[0])} data-testid="receipt-file-input" />
                  <Button type="button" variant="secondary" size="sm" className="rounded-xl shrink-0" onClick={() => fileRef.current?.click()} disabled={scanning} data-testid="receipt-scan-button">
                    {scanning ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Reading…</> : <><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Scan</>}
                  </Button>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Amount</Label>
                    <CurrencyInput
                      currency={currency}
                      value={form.amount}
                      onChange={(e) => setForm({ ...form, amount: e.target.value })}
                      placeholder="0"
                      data-testid="transaction-amount-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Date</Label>
                    <Input type="date" value={form.txn_date} onChange={(e) => setForm({ ...form, txn_date: e.target.value })} className="rounded-xl" data-testid="transaction-date-input" />
                  </div>
                  <div className="sm:col-span-2 space-y-1.5">
                    <Label>{form.type === 'income' ? 'Source' : form.type === 'investment' ? 'Investment name / broker' : form.type === 'transfer' ? 'Description (e.g. ATM withdrawal, transfer to savings)' : form.type === 'refund' ? 'Description (e.g. refund, cashback)' : 'Description / merchant'}</Label>
                    <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} onBlur={onDescriptionBlur} placeholder={form.type === 'income' ? 'e.g. Salary, Freelance project' : form.type === 'investment' ? 'e.g. Index fund, brokerage name' : form.type === 'transfer' ? 'e.g. ATM withdrawal, transfer to savings' : form.type === 'refund' ? 'e.g. Order refund, card cashback' : 'e.g. Uber, groceries, rent'} className="rounded-xl" data-testid="transaction-description-input" list="transaction-description-suggestions" autoComplete="off" />
                    {/* v3.4.6 — native <datalist> autocomplete fed by
                        /api/quick-log/top-merchants. The browser handles
                        prefix filtering + keyboard navigation for free,
                        no combobox library needed. */}
                    <datalist id="transaction-description-suggestions">
                      {descriptionSuggestions.map((s) => (
                        <option key={s.description} value={s.description} />
                      ))}
                    </datalist>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Category</Label>
                    <CategorySelect
                      type={form.type}
                      value={form.category}
                      onChange={(v) => setForm({ ...form, category: v })}
                      testId="transaction-category-select"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Account</Label>
                    {accounts.length > 0 ? (
                      <div className="flex items-center gap-2">
                        <Select value={form.account_id || undefined} onValueChange={(v) => setForm({ ...form, account_id: v })}>
                          <SelectTrigger className="rounded-xl flex-1" data-testid="transaction-account-select"><SelectValue placeholder="Select account" /></SelectTrigger>
                          <SelectContent>
                            {accounts.map((a) => (
                              <SelectItem key={a.id} value={a.id}>
                                <span className="truncate">{a.name}</span>
                                {a.last4 && <span className="ml-1.5 text-muted-foreground tabular-nums text-[11px]">&bull;&bull;{a.last4}</span>}
                                <span className="ml-1.5 text-muted-foreground text-[11px]">&middot; {(a.type || 'bank').replace('_', ' ')}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Link to="/accounts" className="text-[11px] text-primary whitespace-nowrap shrink-0" data-testid="transaction-go-add-account">+ Add</Link>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className="flex-1 rounded-xl border border-dashed border-border/70 bg-muted/30 px-3 py-2 text-xs text-muted-foreground flex items-center gap-2">
                          <Wallet className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">No accounts yet , add one to link this transaction.</span>
                        </div>
                        <Link to="/accounts" className="text-[11px] text-primary whitespace-nowrap shrink-0" data-testid="transaction-add-first-account">+ Add account</Link>
                      </div>
                    )}
                  </div>
                  <div className="sm:col-span-2 space-y-1.5">
                    {/* Phase 81.3 — Optional payment method.
                        Kept off the primary two-column grid so it doesn't
                        crowd the fast-path (Amount → Description →
                        Category → Account is enough). Empty ("Not
                        specified") is the default and stores as NULL. */}
                    <Label htmlFor="txn-payment-method">
                      Payment method{' '}
                      <span className="text-muted-foreground font-normal">(optional)</span>
                    </Label>
                    <Select
                      value={form.payment_method || '__none__'}
                      onValueChange={(v) => setForm({ ...form, payment_method: v === '__none__' ? '' : v })}
                    >
                      <SelectTrigger
                        className="rounded-xl"
                        data-testid="transaction-payment-method-select"
                        id="txn-payment-method"
                      >
                        <SelectValue placeholder="Not specified" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__" data-testid="pm-option-none">Not specified</SelectItem>
                        <SelectItem value="cash" data-testid="pm-option-cash">Cash</SelectItem>
                        <SelectItem value="card" data-testid="pm-option-card">Card</SelectItem>
                        <SelectItem value="upi" data-testid="pm-option-upi">UPI</SelectItem>
                        <SelectItem value="bank_transfer" data-testid="pm-option-bank_transfer">Bank transfer</SelectItem>
                        <SelectItem value="wallet" data-testid="pm-option-wallet">Wallet</SelectItem>
                        <SelectItem value="cheque" data-testid="pm-option-cheque">Cheque</SelectItem>
                        <SelectItem value="other" data-testid="pm-option-other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    {/* Cash-method helper: nudge toward a Cash Wallet account.
                        Only shows when payment_method=cash AND the currently-
                        selected account is not already a cash account. */}
                    {form.payment_method === 'cash' && (() => {
                      const selectedAccount = accounts.find((a) => a.id === form.account_id);
                      const selectedIsCash = (selectedAccount?.type || '').toLowerCase() === 'cash';
                      if (selectedIsCash) return null;
                      const existingCash = accounts.find((a) => (a.type || '').toLowerCase() === 'cash');
                      if (existingCash) {
                        return (
                          <div
                            className="mt-1 flex items-center gap-2 rounded-lg border border-dashed border-primary/30 bg-primary/5 px-3 py-2 text-[11.5px]"
                            data-testid="cash-wallet-hint-existing"
                          >
                            <Wallet className="h-3.5 w-3.5 shrink-0 text-primary" />
                            <span className="flex-1 text-muted-foreground">
                              You have a Cash Wallet — link this to it?
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-[11px] text-primary hover:text-primary"
                              onClick={() => setForm((f) => ({ ...f, account_id: existingCash.id, account: existingCash.name }))}
                              data-testid="cash-wallet-use-existing"
                            >
                              Use {existingCash.name}
                            </Button>
                          </div>
                        );
                      }
                      return (
                        <div
                          className="mt-1 flex items-center gap-2 rounded-lg border border-dashed border-primary/30 bg-primary/5 px-3 py-2 text-[11.5px]"
                          data-testid="cash-wallet-hint-create"
                        >
                          <Wallet className="h-3.5 w-3.5 shrink-0 text-primary" />
                          <span className="flex-1 text-muted-foreground">
                            You haven&rsquo;t added a Cash Wallet yet.
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-[11px] text-primary hover:text-primary"
                            disabled={creatingCashWallet}
                            onClick={createCashWallet}
                            data-testid="cash-wallet-create"
                          >
                            {creatingCashWallet ? (
                              <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Creating…</>
                            ) : (
                              <>+ Create Cash Wallet</>
                            )}
                          </Button>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="sm:col-span-2 flex items-center justify-between rounded-xl border border-border/70 px-3 py-2.5">
                    <div>
                      <div className="text-sm font-medium">Recurring</div>
                      <div className="text-xs text-muted-foreground">Mark monthly subscriptions or bills.</div>
                    </div>
                    <Switch checked={form.recurring} onCheckedChange={(v) => setForm({ ...form, recurring: v })} data-testid="transaction-recurring-switch" />
                  </div>
                  {profile?.is_superadmin && editingId && (
                    <div className="sm:col-span-2 flex items-center justify-between rounded-xl border border-primary/40 bg-primary/[0.04] px-3 py-2.5" data-testid="transaction-promote-global-row">
                      <div>
                        <div className="text-sm font-medium flex items-center gap-1.5">
                          <Shield className="h-3.5 w-3.5 text-primary" />
                          Promote this categorisation to the global rules
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Founder-only. One confirmation from you here is enough to teach the pattern miner across every user.
                        </div>
                      </div>
                      <Switch
                        checked={!!form.promote_global}
                        onCheckedChange={(v) => setForm({ ...form, promote_global: v })}
                        data-testid="transaction-promote-global-switch"
                      />
                    </div>
                  )}
                  <div className="sm:col-span-2 space-y-1.5">
                    <Label>Note <span className="text-muted-foreground font-normal">(optional)</span></Label>
                    <Input
                      value={form.note}
                      onChange={(e) => setForm({ ...form, note: e.target.value })}
                      placeholder="e.g. shared dinner, split with a friend"
                      maxLength={200}
                      className="rounded-xl"
                      data-testid="transaction-note-input"
                    />
                    <div className="text-[10px] text-muted-foreground text-right">{(form.note || '').length}/200</div>
                  </div>
                </div>
                <DialogFooter className="sm:justify-end gap-2">
                  <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="rounded-xl" data-testid="transaction-cancel-button">Cancel</Button>
                  <Button type="submit" className="rounded-xl" disabled={saving} data-testid="transaction-save-button">
                    {saving ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…</> : 'Save'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary chips */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="rounded-2xl border-border/70" data-testid="kpi-income-card"><CardContent className="p-4"><div className="text-xs text-muted-foreground">Income</div><div className="font-display text-lg font-semibold tabular-nums" data-testid="kpi-income-amount">{formatMoney(totals.income, totalsCurrency, { compact: true })}</div>{totals.borrowed > 0 && (
          <div className="mt-1 text-[11px] leading-tight text-muted-foreground" data-testid="kpi-income-borrowed-note">
            {formatMoney(totals.borrowed, totalsCurrency, { compact: true })} borrowed — kept out of income
          </div>
        )}{totals.unresolved > 0 && (
          <div className="mt-1 text-[11px] leading-tight text-muted-foreground" data-testid="kpi-income-unconfirmed-note">
            {formatMoney(totals.unresolved, totalsCurrency, { compact: true })} received — confirm what it was
          </div>
        )}</CardContent></Card>
        <Card className="rounded-2xl border-border/70"><CardContent className="p-4"><div className="text-xs text-muted-foreground">Expenses</div><div className="font-display text-lg font-semibold tabular-nums">{formatMoney(totals.expense, totalsCurrency, { compact: true })}</div></CardContent></Card>
        <Card className="rounded-2xl border-border/70"><CardContent className="p-4"><div className="text-xs text-muted-foreground">Investments</div><div className="font-display text-lg font-semibold tabular-nums">{formatMoney(totals.investment, totalsCurrency, { compact: true })}</div></CardContent></Card>
        <Card className="rounded-2xl border-border/70" data-testid="kpi-refunds-card"><CardContent className="p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs text-muted-foreground flex items-center gap-1.5">Refunds <RotateCcw className="h-3 w-3 opacity-60" /></div>
            {/* Phase 84.20.3 (user #6) — Quick link to the exact rows
                behind this figure. `type=refund` matches the source of
                `totals.refund` (all Refunds & Reversals + Cashback +
                related refund categories are stored as type='refund'). */}
            {totals.refund > 0 && (
              <Link
                to="/transactions?type=refund"
                className="text-[10px] font-medium text-[hsl(var(--primary))] hover:underline underline-offset-2 shrink-0"
                data-testid="kpi-refunds-view-all"
              >
                View all →
              </Link>
            )}
          </div>
          <div className="font-display text-lg font-semibold tabular-nums text-muted-foreground">{formatMoney(totals.refund, totalsCurrency, { compact: true })}</div>
          <div className="text-[10px] text-muted-foreground/80 mt-0.5">Not counted as income</div>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-[1fr_160px_160px_160px_180px] gap-2">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search description, notes, category, account…" className="rounded-xl pl-9" data-testid="transactions-filter-search" />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="rounded-xl" data-testid="transactions-filter-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="expense">Expense</SelectItem>
            <SelectItem value="income">Income</SelectItem>
            <SelectItem value="investment">Investment</SelectItem>
            <SelectItem value="refund">Refund</SelectItem>
            <SelectItem value="transfer">Transfer</SelectItem>
          </SelectContent>
        </Select>
        <CategoryCombobox
          groups={categoryGroups}
          value={filterCat}
          onChange={setFilterCat}
          leadingOptions={[{ value: 'all', label: 'All categories' }]}
          placeholder="All categories"
          triggerClassName="rounded-xl"
          testId="transactions-filter-category"
        />
        {/* Phase 51.2, account filter. Shows the account name with the
            small ••last4 hint when present. */}
        <Select value={filterAccountId} onValueChange={setFilterAccountId}>
          <SelectTrigger className="rounded-xl" data-testid="transactions-filter-account">
            <SelectValue placeholder="All accounts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All accounts</SelectItem>
            {(accounts || []).map((a) => (
              <SelectItem key={a.id} value={a.id}>
                <span className="inline-flex items-center gap-1.5 truncate">
                  <Wallet className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="truncate">{a.name}</span>
                  {a.last4 && <span className="text-muted-foreground tabular-nums text-[10.5px]">&bull;&bull;{a.last4}</span>}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* Phase 84.20 (Beta issue #6) — Sort selector. */}
        <Select value={sortMode} onValueChange={setSortMode}>
          <SelectTrigger className="rounded-xl" data-testid="transactions-filter-sort" aria-label="Sort transactions">
            <SelectValue placeholder="Sort" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="date_desc" data-testid="transactions-sort-date-desc">Newest first</SelectItem>
            <SelectItem value="date_asc" data-testid="transactions-sort-date-asc">Oldest first</SelectItem>
            <SelectItem value="amount_desc" data-testid="transactions-sort-amount-desc">Amount: High → Low</SelectItem>
            <SelectItem value="amount_asc" data-testid="transactions-sort-amount-asc">Amount: Low → High</SelectItem>
            <SelectItem value="merchant_asc" data-testid="transactions-sort-merchant-asc">Merchant: A → Z</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Date range chips */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="transactions-range-chips">
        <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
        {RANGE_PRESETS.map((p) => {
          const isActive = rangePreset === p.value;
          if (p.value === 'custom') {
            return (
              <Popover key={p.value} open={customOpen} onOpenChange={setCustomOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    onClick={() => { setRangePreset('custom'); setCustomOpen(true); }}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${isActive
                      ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]'
                      : 'bg-muted/40 text-muted-foreground hover:bg-muted/70'}`}
                    data-testid={`range-chip-${p.value}`}
                  >
                    {isActive && customStart && customEnd
                      ? `${customStart} → ${customEnd}`
                      : p.label}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="rounded-2xl w-80 p-4 space-y-3" align="start">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {customMode === 'month' ? 'Pick a month' : 'Custom date range'}
                    </div>
                    {/* Phase 84.23.11 — beta users often want to filter by
                        a whole month; the mm/dd/yyyy fields are extra work.
                        Toggle lets them use either shape without leaving
                        the popover. */}
                    <div className="inline-flex items-center rounded-lg border border-border/70 bg-muted/30 p-0.5 text-[11px]" data-testid="range-custom-mode-toggle">
                      <button
                        type="button"
                        onClick={() => setCustomMode('month')}
                        className={`rounded-md px-2 py-0.5 transition-colors ${customMode === 'month' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground'}`}
                        data-testid="range-custom-mode-month"
                      >Month</button>
                      <button
                        type="button"
                        onClick={() => setCustomMode('range')}
                        className={`rounded-md px-2 py-0.5 transition-colors ${customMode === 'range' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground'}`}
                        data-testid="range-custom-mode-range"
                      >Date range</button>
                    </div>
                  </div>

                  {customMode === 'month' ? (
                    /* Month grid picker — matches beta screenshot: year
                       header with prev/next arrows, 3×4 grid of month
                       chips, selected month highlighted. Applying it
                       sets customStart to the 1st of the month and
                       customEnd to the last day. */
                    <div className="space-y-3" data-testid="range-custom-month-picker">
                      <div className="flex items-center justify-between px-1">
                        <button
                          type="button"
                          onClick={() => setCustomMonthYear((y) => y - 1)}
                          className="p-1 rounded-md hover:bg-muted"
                          aria-label="Previous year"
                          data-testid="range-custom-year-prev"
                        >‹</button>
                        <div className="text-sm font-semibold" data-testid="range-custom-year">{customMonthYear}</div>
                        <button
                          type="button"
                          onClick={() => setCustomMonthYear((y) => y + 1)}
                          className="p-1 rounded-md hover:bg-muted"
                          aria-label="Next year"
                          data-testid="range-custom-year-next"
                        >›</button>
                      </div>
                      <div className="grid grid-cols-3 gap-1.5">
                        {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => {
                          const isSel = customMonthYear === customMonthSel.year && i === customMonthSel.month;
                          return (
                            <button
                              key={m}
                              type="button"
                              onClick={() => setCustomMonthSel({ year: customMonthYear, month: i })}
                              className={`rounded-lg px-2 py-2 text-sm font-medium transition-colors ${
                                isSel
                                  ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]'
                                  : 'hover:bg-muted'
                              }`}
                              data-testid={`range-custom-month-${m.toLowerCase()}`}
                            >{m}</button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-1.5">
                        <Label className="text-xs">From</Label>
                        <Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="rounded-xl" max={customEnd || undefined} data-testid="range-custom-from" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">To</Label>
                        <Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="rounded-xl" min={customStart || undefined} data-testid="range-custom-to" />
                      </div>
                    </>
                  )}

                  <div className="flex items-center justify-end gap-2 pt-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-xl"
                      onClick={() => { setCustomStart(''); setCustomEnd(''); }}
                      data-testid="range-custom-clear"
                    >Clear</Button>
                    <Button
                      size="sm"
                      className="rounded-xl"
                      onClick={() => {
                        if (customMode === 'month') {
                          // Materialise month → date range in the
                          // user's LOCAL calendar day, so a user in a
                          // negative-UTC-offset timezone (US) doesn't
                          // see May 1st roll back to Apr 30 via
                          // toISOString(). Build the string manually
                          // from the Date's local components.
                          const y = customMonthSel.year;
                          const m = customMonthSel.month;
                          const lastDay = new Date(y, m + 1, 0).getDate();
                          const mm = String(m + 1).padStart(2, '0');
                          const dd = String(lastDay).padStart(2, '0');
                          setCustomStart(`${y}-${mm}-01`);
                          setCustomEnd(`${y}-${mm}-${dd}`);
                        }
                        setCustomOpen(false);
                      }}
                      data-testid="range-custom-apply"
                    >Apply</Button>
                  </div>
                </PopoverContent>
              </Popover>
            );
          }
          return (
            <button
              key={p.value}
              type="button"
              onClick={() => setRangePreset(p.value)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${isActive
                ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]'
                : 'bg-muted/40 text-muted-foreground hover:bg-muted/70'}`}
              data-testid={`range-chip-${p.value}`}
            >
              {p.label}
            </button>
          );
        })}
        {rangePreset !== 'all' && filtered.length > 0 && (
          <span className="text-[11px] text-muted-foreground ml-1">{filtered.length} {filtered.length === 1 ? 'transaction' : 'transactions'}</span>
        )}
      </div>

      {/* v3.19.4 — what's actually narrowing the ledger right now. Date range
          is excluded on purpose: it has its own preset row directly above. */}
      <ActiveFilterChips
        resultCount={filtered.length}
        onClearAll={clearAllFilters}
        filters={[
          search && { key: 'search', label: 'Search', value: `“${search}”`, onRemove: () => setSearch('') },
          filterType !== 'all' && {
            key: 'type', label: 'Type',
            value: filterType.charAt(0).toUpperCase() + filterType.slice(1),
            onRemove: () => setFilterType('all'),
          },
          filterCat !== 'all' && { key: 'category', label: 'Category', value: filterCat, onRemove: () => setFilterCat('all') },
          filterAccountId !== 'all' && {
            key: 'account', label: 'Account',
            value: accountsById.get(filterAccountId)?.name || 'Unknown account',
            onRemove: () => setFilterAccountId('all'),
          },
        ]}
      />

      {/* List */}
      {loading ? (
        <div className="space-y-2"><Skeleton className="h-14 rounded-xl" /><Skeleton className="h-14 rounded-xl" /><Skeleton className="h-14 rounded-xl" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border/70 p-10 text-center" data-testid="transactions-empty-state">
          <div className="font-display text-lg font-semibold">Your story hasn&rsquo;t started yet</div>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">Add a transaction, scan a receipt, or import a statement to start building a picture of your money.</p>
          <div className="mt-4 flex items-center justify-center gap-2">
            <Button onClick={() => setOpen(true)} className="rounded-xl" data-testid="empty-add-first-tx"><Plus className="h-4 w-4 mr-1" /> Add transaction</Button>
            <Link to="/import"><Button variant="secondary" className="rounded-xl"><Upload className="h-4 w-4 mr-1" /> Import Statements</Button></Link>
          </div>
        </div>
      ) : (
        <div className="space-y-6" data-testid="transactions-day-groups">
          {/* Phase 84.19.2 — Compact "Select all filtered" pill. Shown any
              time the user has ≥1 row selected, so they can promote to
              "all filtered" in one click without hunting down individual
              checkboxes. Also acts as a visible affordance for the bulk-
              select feature — beta feedback said users didn't realise
              transactions were selectable. */}
          {filtered.length > 0 && (
            <div
              className="flex items-center gap-2 text-[11px] text-muted-foreground -mb-2"
              data-testid="transactions-select-all-row"
            >
              <button
                type="button"
                onClick={() => {
                  const allIds = filtered.map((t) => t.id);
                  const allSelected = allIds.every((id) => selectedIds.has(id));
                  setSelectedIds(allSelected ? new Set() : new Set(allIds));
                }}
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="transactions-select-all-button"
              >
                <span className={`inline-block h-3.5 w-3.5 rounded-[4px] border transition-colors ${
                  filtered.length > 0 && filtered.every((t) => selectedIds.has(t.id))
                    ? 'bg-primary border-primary'
                    : selectedIds.size > 0
                      ? 'border-primary bg-primary/25'
                      : 'border-muted-foreground/50'
                }`}
                aria-hidden="true"
                />
                {filtered.length > 0 && filtered.every((t) => selectedIds.has(t.id))
                  ? `Clear ${selectedIds.size} selected`
                  : selectedIds.size > 0
                    ? `${selectedIds.size} selected — select all ${filtered.length}?`
                    : `Select all ${filtered.length}`}
              </button>
            </div>
          )}

          {/* Phase 84.19.3 — Inline bulk-action bar directly below the
              Select-all pill. Beta feedback: the sticky-bottom variant
              was easy to miss on long ledgers and got clipped by mobile
              Safari's home indicator. Docking here puts the actions
              exactly where the user's attention already is. */}
          <TxnBulkActionBar
            variant="inline"
            count={selectedIds.size}
            onClear={clearSelection}
            onRecategorise={bulkRecategorise}
            onMarkReviewed={bulkMarkReviewed}
            onLinkRecurring={bulkLinkRecurring}
            onDelete={bulkDelete}
            recurringOptions={recurringOptions}
          />

          {/* Phase 3 — "Needs your attention" band. Renders null when there's
              nothing to surface. */}
          <TxnAttentionBand
            reconciliationCount={reconcileCount}
            lowConfidence={lowConfidenceTxns}
            unresolvedInflows={unresolvedInflowTxns}
            onOpenTxn={(id) => {
              // Phase 84.20 P1-3 — Look up the transaction in the FULL
              // list first, falling back to filtered. Bug reported by
              // beta user: the Fix/Review buttons did nothing because
              // low-confidence rows often live outside the currently
              // active date range/filter, so `filtered.find(...)` was
              // returning undefined and the click was silently swallowed.
              const t = txns.find((x) => x.id === id) || filtered.find((x) => x.id === id);
              if (t) openDrawerFor(t);
            }}
          />

          {isDateSort ? (
            groupedByDay.map((g) => (
              <section key={g.key} data-testid={`txn-day-${g.key}`}>
                {/* Sticky day header. `top` is set high enough to clear the
                    page header + filter row on scroll; adjust if the shell
                    height changes. */}
                <div
                  className={`sticky ${selectedIds.size > 0 ? 'top-[9rem]' : 'top-14'} z-10 flex items-baseline justify-between mb-2 px-1 py-1.5 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/70 border-b border-border/40`}
                  data-testid={`txn-day-header-${g.key}`}
                >
                  <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {dayLabel(g.key)}
                  </h3>
                  <div className="flex items-center gap-3 text-[11px] text-muted-foreground tabular-nums">
                    {g.income > 0 && <span className="text-[hsl(var(--chart-5))]">+{formatMoney(g.income, currency, { compact: true })}</span>}
                    {g.expense > 0 && <span>−{formatMoney(g.expense, currency, { compact: true })}</span>}
                    {g.investment > 0 && <span>↑{formatMoney(g.investment, currency, { compact: true })}</span>}
                    {g.refund > 0 && <span>↺{formatMoney(g.refund, currency, { compact: true })}</span>}
                    <span className="opacity-60">· {g.items.length}</span>
                  </div>
                </div>
                <div className="space-y-1.5">
                  {g.items.map((t) => (
                    <TxnRow
                      key={t.id}
                      txn={t}
                      accountName={displayAccount(t)}
                      currency={(accountsById.get(t.account_id)?.currency || currency).toUpperCase()}
                      selected={selectedIds.has(t.id)}
                      selectionMode={selectionMode}
                      onToggleSelect={toggleSelect}
                      onOpen={openDrawerFor}
                      matchedByNote={matchedByNoteIds.has(t.id)}
                    />
                  ))}
                </div>
              </section>
            ))
          ) : (
            /* Phase 84.20 (Beta #6 follow-up) — Flat, non-grouped list for
               non-date sorts. A subtle header shows which sort is active
               and how many rows are in view. */
            <section data-testid="txn-flat-list">
              <div className="flex items-baseline justify-between mb-2 px-1 py-1.5 border-b border-border/40">
                <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {sortMode === 'amount_desc' && 'Sorted by amount: high → low'}
                  {sortMode === 'amount_asc'  && 'Sorted by amount: low → high'}
                  {sortMode === 'merchant_asc' && 'Sorted by merchant: A → Z'}
                </h3>
                <div className="text-[11px] text-muted-foreground tabular-nums">
                  {filtered.length} transaction{filtered.length === 1 ? '' : 's'}
                </div>
              </div>
              <div className="space-y-1.5">
                {filtered.map((t) => (
                  <TxnRow
                    key={t.id}
                    txn={t}
                    accountName={displayAccount(t)}
                    currency={(accountsById.get(t.account_id)?.currency || currency).toUpperCase()}
                    selected={selectedIds.has(t.id)}
                    selectionMode={selectionMode}
                    onToggleSelect={toggleSelect}
                    onOpen={openDrawerFor}
                    matchedByNote={matchedByNoteIds.has(t.id)}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {/* Phase 3 — Detail drawer for viewing / quick-editing an existing
          transaction. Replaces the legacy edit modal for the common path;
          "Advanced editor…" inside the drawer still opens the legacy dialog
          for surfaces we haven't ported yet. */}
      <TxnDrawer
        txn={drawerTxn}
        open={drawerOpen}
        onOpenChange={(v) => { setDrawerOpen(v); if (!v) setDrawerTxn(null); }}
        accountsById={accountsById}
        accountsList={accounts}
        currency={(drawerTxn && accountsById.get(drawerTxn.account_id)?.currency) || currency}
        matchedRecurring={matchedRecurringForDrawer}
        recurringOptions={recurringOptions}
        onSave={saveFromDrawer}
        onDelete={deleteFromDrawer}
        onMarkReviewed={markReviewedFromDrawer}
        onLinkRecurring={linkRecurringFromDrawer}
        onUnlinkRecurring={unlinkRecurringFromDrawer}
        onOpenAdvancedEditor={() => {
          if (drawerTxn) {
            setDrawerOpen(false);
            openForEdit(drawerTxn);
            setOpen(true);
          }
        }}
      />

      {/* A new wallet's currency is proposed, never assumed. */}
      <Dialog open={!!walletProposal} onOpenChange={(o) => !o && setWalletProposal(null)}>
        <DialogContent className="sm:max-w-md" data-testid="cash-wallet-currency-dialog">
          <DialogHeader>
            <DialogTitle>Which currency is this wallet in?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {walletProposal?.from
              ? <>We have suggested <span className="font-medium">{walletProposal.code}</span>,
                  based on {walletProposal.from}. Change it if the cash you hold is
                  in something else — we will not assume.</>
              : <>We do not know which currency you hold cash in, so please choose one.</>}
          </p>
          <Select
            value={walletProposal?.code || undefined}
            onValueChange={(v) => setWalletProposal((p) => ({ ...p, code: v }))}
          >
            <SelectTrigger className="rounded-xl" data-testid="cash-wallet-currency-select">
              <SelectValue placeholder="Choose a currency…" />
            </SelectTrigger>
            <SelectContent>
              {CURRENCIES.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {c.code} — {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setWalletProposal(null)}
                    data-testid="cash-wallet-currency-cancel">
              Cancel
            </Button>
            <Button
              disabled={!walletProposal?.code || creatingCashWallet}
              onClick={() => confirmCashWallet(walletProposal?.code)}
              data-testid="cash-wallet-currency-confirm"
            >
              {creatingCashWallet ? 'Creating…' : 'Create wallet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 84.19.3 — The bottom sticky bulk-action bar was moved
          inline (see above, next to the Select-all pill). Beta users
          reported that a bottom sticky bar on long ledgers was too far
          from where their attention was, and got clipped by mobile
          Safari's home indicator. */}
    </div>
  );
}
